import axios from 'axios';
import subtractMonths from 'date-fns/sub_months';
import subtractYears from 'date-fns/sub_years';
import {
  pipe,
  path,
  partial,
  filter,
  prop,
  map,
  find,
  propEq,
} from 'ramda';

import { formatDateString } from 'utils/formatString';

import {
  FETCH_MESSAGES,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_ERROR,
  CHANGE_ACTIVE_FILTER,
  DELETE_NOTIFICATIONS,
  DELETE_NOTIFICATIONS_SUCCESS,
  DELETE_NOTIFICATIONS_ERROR,
  MARK_NOTIFICATION_AS_READ,
  MARK_NOTIFICATION_AS_READ_SUCCESS,
  MARK_NOTIFICATION_AS_READ_ERROR,
  GET_REQUEST_DETAILS,
  GET_REQUEST_DETAILS_SUCCESS,
  GET_REQUEST_DETAILS_ERROR,
  UPDATE_PERIOD,
  PERIOD_MONTH,
  PERIOD_YEAR,
  RESET_ERROR,
} from './constants';

import {
  isNotificationUnread,
  isDetailsRequired,
} from './common';

const deleteNotificationsFromState = pipe(
  path(['messages', 'table', 'data']),
  filter(prop('selected')),
  map(prop('id')),
  (notificationIds) => ({ notificationIds }),
  partial(axios.post, ['/api/profile/messages/deletenotification/']),
);

export function fetchMessagesStart() {
  return {
    type: FETCH_MESSAGES,
  };
}

export function fetchMessagesSuccess(data) {
  return {
    data,
    type: FETCH_MESSAGES_SUCCESS,
  };
}

export function fetchMessagesError(error) {
  return {
    error,
    type: FETCH_MESSAGES_ERROR,
  };
}

export function deleteNotificationsStart() {
  return {
    type: DELETE_NOTIFICATIONS,
  };
}

export function deleteNotificationsSuccess() {
  return {
    type: DELETE_NOTIFICATIONS_SUCCESS,
  };
}

export function deleteNotificationsError(error) {
  return {
    error,
    type: DELETE_NOTIFICATIONS_ERROR,
  };
}

export function markNotificationAsReadStart() {
  return {
    type: MARK_NOTIFICATION_AS_READ,
  };
}

export function markNotificationAsReadSuccess(notificationId) {
  return {
    data: notificationId,
    type: MARK_NOTIFICATION_AS_READ_SUCCESS,
  };
}

export function markNotificationAsReadError(error) {
  return {
    error,
    type: MARK_NOTIFICATION_AS_READ_ERROR,
  };
}

export function getRequestDetailsStart() {
  return {
    type: GET_REQUEST_DETAILS,
  };
}

export function getRequestDetailsSuccess(data) {
  return {
    data,
    type: GET_REQUEST_DETAILS_SUCCESS,
  };
}

export function getRequestDetailsError(error) {
  return {
    error,
    type: GET_REQUEST_DETAILS_ERROR,
  };
}

export function updatePeriod() {
  return {
    type: UPDATE_PERIOD,
  };
}

export function changeActiveFilter(data) {
  return {
    data,
    type: CHANGE_ACTIVE_FILTER,
  };
}

export function resetError() {
  return {
    type: RESET_ERROR,
  };
}

export function fetchMessages(noTimeout = false) {
  return async (dispatch, getState) => {
    dispatch(fetchMessagesStart());

    const { period } = getState().messages;
    let dateFrom;
    switch (period) {
      case PERIOD_MONTH:
        dateFrom = formatDateString(subtractMonths(new Date(), 1), 'YYYY-MM-DD');
        break;
      case PERIOD_YEAR:
        dateFrom = formatDateString(subtractYears(new Date(), 1), 'YYYY-MM-DD');
        break;
      default:
        dateFrom = null;
    }

    try {
      const { data } = await axios.get('/api/profile/messages/get/', {
        params: { noTimeout, dateFrom },
      });
      dispatch(fetchMessagesSuccess(data));
    } catch (error) {
      dispatch(fetchMessagesError(error.message));
    }
  };
}

export function deleteNotifications() {
  return async (dispatch, getState) => {
    dispatch(deleteNotificationsStart());

    try {
      await deleteNotificationsFromState(getState());

      dispatch(deleteNotificationsSuccess());

      dispatch(fetchMessages());
    } catch (error) {
      dispatch(deleteNotificationsError(error.message));
    }
  };
}

export function markNotificationAsRead(notificationId) {
  return async (dispatch) => {
    dispatch(markNotificationAsReadStart());

    try {
      await axios.post('/api/profile/messages/setnotificationread/', {
        notificationIds: [notificationId],
      });

      dispatch(markNotificationAsReadSuccess(notificationId));
    } catch (error) {
      dispatch(markNotificationAsReadError(error.message));
    }
  };
}

export function getRequestDetails(requestId, isFttb, isHelpdesk = null) {
  return async (dispatch) => {
    dispatch(getRequestDetailsStart());

    try {
      const { data: requestDetails } = await axios.get('/api/profile/messages/requestdetails/', {
        params: { requestId, isFttb, isHelpdesk },
      });

      dispatch(getRequestDetailsSuccess({ requestId, requestDetails }));
    } catch (error) {
      dispatch(getRequestDetailsError(error.message));
    }
  };
}

export function updateMessageIfNeeded({ id, isExpanded }) {
  return (dispatch, getState) => {
    if (isExpanded) {
      const message = find(propEq('id', id), path(['messages', 'items'], getState()));
      const { isFttb, isHelpdesk } = message;

      if (isNotificationUnread(message)) {
        dispatch(markNotificationAsRead({ notificationId: id, isFttb }));
      }

      if (isDetailsRequired(message)) {
        dispatch(getRequestDetails(id, isFttb, isHelpdesk));
      }
    }
  };
}

export function fetchAllMessages() {
  return (dispatch) => {
    dispatch(updatePeriod());
    dispatch(fetchMessages());
  };
}

export function fetchInitialData(noTimeout) {
  return (dispatch) => {
    dispatch(resetError());
    dispatch(fetchMessages(noTimeout));
  };
}
